<template>
  <v-dialog
    v-model="dialog"
    max-width="960"
    persistent>

    <v-card class="dialog-card">

      <!-- ── Toolbar ──────────────────────────────────────────────── -->
      <v-toolbar flat class="dialog-toolbar" height="56">
        <div class="toolbar-icon-wrap mr-3">
          <v-icon color="primary" size="20">mdi-file-table-outline</v-icon>
        </div>
        <div class="toolbar-title-block">
          <div class="toolbar-title">Actas Centralizadas</div>
          <div v-if="programaNombre" class="toolbar-subtitle text-truncate">
            {{ programaNombre }}
          </div>
        </div>
        <v-spacer />
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on" class="mr-2">
              <v-btn
                v-if="tabKey === 'universidad'"
                small
                color="teal"
                dark
                depressed
                class="mr-2"
                :loading="descargando"
                :disabled="!pdfUrls[tabKey]"
                @click="descargarPdf">
                <v-icon left small>mdi-sync</v-icon>
                Sincronizar
            </v-btn>
            </span>
          </template>
          <span>Próximamente disponible</span>
        </v-tooltip>        
        <v-btn
          small
          color="error"
          dark
          depressed
          class="mr-2"
          :loading="descargando"
          :disabled="!pdfUrls[tabKey] || sinEnlaceUniversidad"
          @click="descargarPdf">
          <v-icon left small>mdi-file-pdf-box</v-icon>
          Descargar PDF
        </v-btn>
        <v-btn icon small @click="cerrar">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <!-- ── Tabs ─────────────────────────────────────────────────── -->
      <v-tabs
        v-model="tabActiva"
        background-color="white"
        color="primary"
        slider-color="primary">
        <v-tab v-for="tab in tabs" :key="tab.value">
          <v-icon x-small left>{{ tab.icon }}</v-icon>
          {{ tab.label }}
        </v-tab>
      </v-tabs>
      <v-divider />

      <!-- ── Contenido del tab activo ──────────────────────────────── -->
      <div class="dialog-body">

        <!-- Sin enlace universidad -->
        <div v-if="sinEnlaceUniversidad" class="pdf-state">
          <v-icon size="52" color="orange darken-1">mdi-link-variant-off</v-icon>
          <p class="state-title mt-3">Programa no enlazado</p>
          <p class="state-text mt-1">
            Este programa no está enlazado con un programa de universidad.<br>
            Para visualizar el acta centralizada o sincronizar, primero debes
            <strong>enlazarlo con un programa de universidad</strong>.
          </p>
        </div>

        <!-- Loading -->
        <div v-else-if="loadingTabs[tabKey]" class="pdf-state">
          <v-progress-circular indeterminate color="primary" size="44" width="3" />
          <p class="state-text mt-3">Cargando acta...</p>
        </div>

        <!-- Error -->
        <div v-else-if="errorTabs[tabKey]" class="pdf-state">
          <v-icon size="48" color="error">mdi-alert-circle-outline</v-icon>
          <p class="state-text mt-2">{{ errorTabs[tabKey] }}</p>
          <v-btn small depressed color="primary" class="mt-2" @click="cargarPdf(tabKey)">
            <v-icon left small>mdi-refresh</v-icon>
            Reintentar
          </v-btn>
        </div>

        <!-- PDF embebido -->
        <iframe
          v-else-if="pdfUrls[tabKey]"
          :src="pdfUrls[tabKey]"
          class="pdf-iframe"
          frameborder="0" />

        <!-- Vacío: esperando carga -->
        <div v-else class="pdf-state">
          <v-progress-circular indeterminate color="grey lighten-2" size="36" width="2" />
          <p class="state-text mt-3">Preparando...</p>
        </div>

      </div>

    </v-card>
  </v-dialog>
</template>

<script>
import CalificacionCentralizadoService from '../../services/calificacion-centralizado.service';

const SERVICIO_MAP = {
  esam:        (programaId) => CalificacionCentralizadoService.getProgramaKardex({ formato: 'pdf', programaId }),
  universidad: (programaId) => CalificacionCentralizadoService.getProgramaKardex({ formato: 'pdf', programaId }),
};

export default {
  name: 'DialogCalificacionCentralizado',
  data() {
    return {
      dialog: false,
      programaId: null,
      programaNombre: null,
      programaUniversidadId: null,
      tabActiva: 0,
      tabs: [
        { value: 'esam',        label: 'Centralizado ESAM',       icon: 'mdi-school-outline' },
        { value: 'universidad', label: 'Centralizado Universidad', icon: 'mdi-domain' },
      ],
      pdfUrls: {},
      loadingTabs: {},
      errorTabs: {},
      descargando: false,
    };
  },
  computed: {
    tabKey() {
      return this.tabs[this.tabActiva]?.value || 'esam';
    },
    sinEnlaceUniversidad() {
      return this.tabKey === 'universidad' && !this.programaUniversidadId;
    },
  },
  watch: {
    tabActiva() {
      this.cargarPdf(this.tabKey);
    },
  },
  methods: {
    abrir(programaId, programaNombre, programaUniversidadId = null) {
      this.programaId = programaId;
      this.programaNombre = programaNombre || null;
      this.programaUniversidadId = programaUniversidadId;
      this.pdfUrls = {};
      this.loadingTabs = {};
      this.errorTabs = {};
      this.descargando = false;
      this.tabActiva = 0;
      this.dialog = true;
      this.$nextTick(() => this.cargarPdf(this.tabKey));
    },
    cerrar() {
      Object.values(this.pdfUrls).forEach((url) => URL.revokeObjectURL(url));
      this.dialog = false;
      this.programaId = null;
      this.programaNombre = null;
      this.programaUniversidadId = null;
      this.pdfUrls = {};
      this.loadingTabs = {};
      this.errorTabs = {};
    },
    async cargarPdf(tipo) {
      if (!this.programaId) return;
      if (tipo === 'universidad' && !this.programaUniversidadId) return;
      if (this.pdfUrls[tipo] || this.loadingTabs[tipo]) return;

      this.$set(this.loadingTabs, tipo, true);
      this.$set(this.errorTabs, tipo, null);

      try {
        const response = await SERVICIO_MAP[tipo](this.programaId);
        const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        this.$set(this.pdfUrls, tipo, url);
      } catch (error) {
        const mensaje = error?.response?.data?.mensaje || error.message || 'Error al obtener el acta';
        this.$set(this.errorTabs, tipo, mensaje);
      } finally {
        this.$set(this.loadingTabs, tipo, false);
      }
    },
    descargarPdf() {
      const tipo = this.tabKey;
      const url = this.pdfUrls[tipo];
      if (!url || !this.programaId) return;

      this.descargando = true;
      const tab = this.tabs.find((t) => t.value === tipo);
      const nombreTab = tab ? tab.label.replace(/\s+/g, '-').toLowerCase() : tipo;
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `acta-${nombreTab}-${this.programaId}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      this.descargando = false;
    },
  },
};
</script>

<style scoped>
/* ── Dialog card ───────────────────────────────────────────── */
.dialog-card {
  border-radius: 12px !important;
  overflow: hidden;
}

/* ── Toolbar ────────────────────────────────────────────────── */
.dialog-toolbar {
  background: linear-gradient(135deg, #eef3ff 0%, #f8fafc 100%) !important;
  border-bottom: 1px solid #dce6f0;
}

.toolbar-icon-wrap {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.toolbar-title-block {
  overflow: hidden;
}

.toolbar-title {
  font-size: 13px;
  font-weight: 700;
  color: #1a2640;
  line-height: 1.2;
}

.toolbar-subtitle {
  font-size: 11px;
  color: #6b7a8d;
  max-width: 420px;
}

/* ── Área de contenido ──────────────────────────────────────── */
.dialog-body {
  min-height: 68vh;
}

/* ── PDF iframe ─────────────────────────────────────────────── */
.pdf-iframe {
  width: 100%;
  height: 68vh;
  display: block;
  border: none;
}

/* ── Estados (loading / error) ──────────────────────────────── */
.pdf-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 68vh;
  padding: 40px 16px;
  background: linear-gradient(180deg, #f8fafc 0%, #fff 60%);
}

.state-title {
  font-size: 15px;
  font-weight: 700;
  color: #1a2640;
  margin: 0;
  text-align: center;
}

.state-text {
  font-size: 13px;
  color: #6b7280;
  margin: 0;
  text-align: center;
}
</style>
